import { useEffect, useRef, useState, useCallback } from 'react';
import JsSIP from 'jssip';
import api from '../api/axios';

export interface SipConfig {
  extension: string;
  password: string;
  wsUrl: string;
  sipDomain: string;
}

export type CallState = 'idle' | 'registering' | 'registered' | 'calling' | 'ringing' | 'active' | 'error';

interface IncomingCallInfo {
  from: string;
  displayName: string;
  session: any;
}

const DEFAULT_WS_URL = `wss://${(window as any).keyoffDesktop ? new URL((window as any).keyoffDesktop.serverOrigin).host : window.location.host}/ws-pbx`;
const DEFAULT_SIP_DOMAIN = '85.202.193.35';


// Шаги работы софтфона уходят в лог сервера (диагностика ответа на звонки)
function report(ext: string | undefined, event: string, detail?: any) {
  api.post('/telephony/client-log', { event, detail, ext }).catch(() => {});
}

// Pre-process raw SIP messages to fix known JsSIP parser issues.
// VitalPBX uses "35735-restored" as internal SIP hostname — this starts with a digit
// and has no dot, which violates RFC 3261 toplabel rule. JsSIP rejects it and disconnects.
function patchSipMessage(data: string): string {
  // Fix invalid VitalPBX internal hostname -> real server IP
  let out = data.replace(/@35735-restored/g, '@85.202.193.35');
  // Fix empty display name: From: "" <sip:...> → From: <sip:...>
  out = out.replace(/((?:^|\r\n)From:[ \t]*)""([ \t]*<)/gm, '$1$2');
  return out;
}

export function useWebRTC() {
  const uaRef = useRef<JsSIP.UA | null>(null);
  const sessionRef = useRef<any>(null);
  const localAudioRef = useRef<HTMLAudioElement | null>(null);
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);

  const [callState, setCallState] = useState<CallState>('idle');
  const [sipConfig, setSipConfig] = useState<SipConfig | null>(() => {
    try {
      const saved = localStorage.getItem('sip_config');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });
  const [incomingCall, setIncomingCall] = useState<IncomingCallInfo | null>(null);
  const [activeNumber, setActiveNumber] = useState<string>('');
  const [error, setError] = useState<string>('');

  const stopUA = useCallback(() => {
    if (uaRef.current) {
      try { uaRef.current.stop(); } catch {}
      uaRef.current = null;
    }
    setCallState('idle');
    setIncomingCall(null);
    sessionRef.current = null;
  }, []);

  const register = useCallback((config: SipConfig) => {
    stopUA();
    setError('');

    localStorage.setItem('sip_config', JSON.stringify(config));
    setSipConfig(config);

    // Enable JsSIP debug logging to browser console
    JsSIP.debug.enable('JsSIP:*');

    const socket = new JsSIP.WebSocketInterface(config.wsUrl || DEFAULT_WS_URL);

    const ua = new JsSIP.UA({
      sockets: [socket],
      uri: `sip:${config.extension}@${config.sipDomain || DEFAULT_SIP_DOMAIN}`,
      password: config.password,
      register: true,
      register_expires: 300,
      session_timers: false,
      user_agent: 'CRM WebPhone',
    });

    ua.on('registered', () => { setCallState('registered'); report(config.extension, 'registered'); });
    ua.on('disconnected', (e: any) => report(config.extension, 'ws_disconnected', `code=${e?.code} reason=${e?.reason}`));
    ua.on('unregistered', () => setCallState('idle'));
    ua.on('registrationFailed', (e: any) => {
      setCallState('error');
      setError(`Ошибка регистрации: ${e.cause}`);
    });

    ua.on('newRTCSession', (e: any) => {
      const session = e.session;
      console.log('[WebRTC] newRTCSession direction:', session.direction, 'from:', session.remote_identity?.uri?.toString());

      if (session.direction === 'incoming') {
        sessionRef.current = session;
        const callerNum = session.remote_identity?.uri?.user ?? 'Неизвестный';
        const displayName = session.remote_identity?.display_name ?? callerNum;
        console.log('[WebRTC] Incoming call from:', callerNum);
        report(config.extension, 'incoming_invite', { from: callerNum });
        setIncomingCall({ from: callerNum, displayName, session });
        setCallState('ringing');

        session.on('ended', () => {
          setCallState('registered');
          setIncomingCall(null);
          sessionRef.current = null;
          setActiveNumber('');
        });
        session.on('failed', () => {
          setCallState('registered');
          setIncomingCall(null);
          sessionRef.current = null;
        });
      } else {
        // outgoing
        sessionRef.current = session;
        setCallState('calling');

        session.on('progress', () => setCallState('calling'));
        session.on('accepted', () => setCallState('active'));
        session.on('confirmed', () => setCallState('active'));
        session.on('ended', () => {
          setCallState('registered');
          setActiveNumber('');
          sessionRef.current = null;
        });
        session.on('failed', (ev: any) => {
          setCallState('registered');
          setError(`Звонок не состоялся: ${ev.cause}`);
          setActiveNumber('');
          sessionRef.current = null;
        });

        // Attach remote audio when available
        session.connection?.addEventListener('track', (ev: RTCTrackEvent) => {
          if (!remoteAudioRef.current) {
            remoteAudioRef.current = new Audio();
            remoteAudioRef.current.autoplay = true;
          }
          remoteAudioRef.current.srcObject = ev.streams[0];
        });
      }
    });

    setCallState('registering');
    ua.start();
    uaRef.current = ua;

    // Patch the underlying WebSocket to fix malformed From headers before JsSIP parses them
    const ws = (socket as any)._ws as WebSocket | undefined;
    if (ws) {
      const origOnMessage = ws.onmessage;
      ws.onmessage = (event: MessageEvent) => {
        if (typeof event.data === 'string') {
          const patched = patchSipMessage(event.data);
          if (patched !== event.data) {
            console.log('[WebRTC] Patched SIP From header');
            origOnMessage?.call(ws, new MessageEvent('message', { data: patched, origin: event.origin, lastEventId: event.lastEventId }));
            return;
          }
        }
        origOnMessage?.call(ws, event);
      };
    }
  }, [stopUA]);

  // Auto-register on mount if config exists
  useEffect(() => {
    if (sipConfig) {
      register(sipConfig);
    }
    return () => { stopUA(); };
  }, []); // eslint-disable-line

  const call = useCallback((target: string) => {
    if (!uaRef.current || callState !== 'registered') return;
    setError('');

    const cleaned = target.replace(/\D/g, '');
    const num = cleaned.startsWith('8') && cleaned.length === 11
      ? '7' + cleaned.slice(1) : cleaned;

    setActiveNumber(num);

    const session = uaRef.current.call(`sip:${num}@${sipConfig?.sipDomain || DEFAULT_SIP_DOMAIN}`, {
      mediaConstraints: { audio: true, video: false },
      rtcOfferConstraints: { offerToReceiveAudio: true, offerToReceiveVideo: false },
      pcConfig: {
        iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
      },
    });

    // Attach remote audio
    (session as any).connection?.addEventListener('track', (ev: RTCTrackEvent) => {
      if (!remoteAudioRef.current) {
        remoteAudioRef.current = new Audio();
        remoteAudioRef.current.autoplay = true;
      }
      remoteAudioRef.current.srcObject = ev.streams[0];
    });
  }, [callState, sipConfig]);

  const answer = useCallback(async () => {
    const session = sessionRef.current;
    const ext = sipConfig?.extension;
    if (!session || callState !== 'ringing') {
      report(ext, 'answer_ignored', { callState, hasSession: !!session });
      return;
    }
    setIncomingCall(null);
    setCallState('active');
    setError('');
    report(ext, 'answer_clicked');

    // 1. Микрофон запрашиваем сами: так видно, если доступ запрещён или окно разрешения скрыто
    let stream: MediaStream;
    const t0 = Date.now();
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      report(ext, 'mic_ok', { ms: Date.now() - t0 });
    } catch (e: any) {
      report(ext, 'mic_failed', `${e?.name}: ${e?.message}`);
      setError('Нет доступа к микрофону. Разрешите микрофон для сайта (значок замка в адресной строке) и повторите.');
      setCallState('registered');
      try { session.terminate({ status_code: 486, reason_phrase: 'Busy Here' }); } catch {}
      return;
    }

    session.on('peerconnection', (e: any) => {
      const pc: RTCPeerConnection = e.peerconnection;
      pc.addEventListener('track', (ev: RTCTrackEvent) => {
        report(ext, 'remote_track', { kind: ev.track.kind });
        if (!remoteAudioRef.current) remoteAudioRef.current = new Audio();
        remoteAudioRef.current.srcObject = ev.streams[0];
        remoteAudioRef.current.play().catch((err) => report(ext, 'audio_play_blocked', String(err?.name)));
      });
      pc.addEventListener('iceconnectionstatechange', () => report(ext, 'ice_connection', pc.iceConnectionState));
      pc.addEventListener('icegatheringstatechange', () => report(ext, 'ice_gathering', pc.iceGatheringState));
      pc.addEventListener('connectionstatechange', () => report(ext, 'connection', pc.connectionState));
    });

    // 2. Не ждём полного сбора ICE-кандидатов (недоступный TURN задерживал ответ дольше таймаута звонка)
    let readyFn: (() => void) | null = null;
    let readySent = false;
    const sendReady = () => { if (!readySent && readyFn) { readySent = true; readyFn(); report(ext, 'ice_ready_sent', { ms: Date.now() - t0 }); } };
    session.on('icecandidate', (d: any) => {
      readyFn = d.ready;
      if (d.candidate?.type === 'srflx' || d.candidate?.type === 'relay') sendReady();
    });
    const started = Date.now();
    const timer = setInterval(() => {
      if (readySent || Date.now() - started > 8000) { clearInterval(timer); return; }
      if (readyFn && Date.now() - started > 1500) { sendReady(); clearInterval(timer); }
    }, 250);

    session.on('accepted', () => report(ext, 'accepted'));
    session.on('confirmed', () => { clearInterval(timer); report(ext, 'confirmed'); });
    session.on('failed', (ev: any) => {
      clearInterval(timer);
      report(ext, 'failed', `${ev?.cause} ${ev?.message?.status_code ?? ''}`);
      setError(`Звонок не соединился: ${ev?.cause ?? 'ошибка'}`);
    });
    session.on('ended', (ev: any) => { clearInterval(timer); report(ext, 'ended', ev?.cause); });

    try {
      session.answer({
        mediaStream: stream,
        pcConfig: {
          iceServers: [
            { urls: 'stun:stun.l.google.com:19302' },
            { urls: 'stun:stun1.l.google.com:19302' },
            { urls: 'turn:crm.keyoff.kz:3478', username: 'crm', credential: 'TurnPass2024!' },
            { urls: 'turn:crm.keyoff.kz:3478?transport=tcp', username: 'crm', credential: 'TurnPass2024!' },
            { urls: 'turns:crm.keyoff.kz:5349', username: 'crm', credential: 'TurnPass2024!' },
          ],
        },
      });
      report(ext, 'answer_called');
    } catch (e: any) {
      clearInterval(timer);
      report(ext, 'answer_threw', `${e?.name}: ${e?.message}`);
      setError(`Не удалось ответить: ${e?.message ?? e}`);
      setCallState('registered');
    }
  }, [callState, sipConfig]);

  const hangup = useCallback(() => {
    if (!sessionRef.current) return;
    try {
      if (callState === 'ringing' && sessionRef.current.direction === 'incoming') {
        sessionRef.current.terminate();
      } else {
        sessionRef.current.terminate();
      }
    } catch {}
    setCallState('registered');
    setIncomingCall(null);
    setActiveNumber('');
    sessionRef.current = null;
  }, [callState]);

  return {
    callState,
    sipConfig,
    incomingCall,
    activeNumber,
    error,
    register,
    unregister: stopUA,
    call,
    answer,
    hangup,
  };
}
